<template>
  <MglMap
    :access-token="accessToken"
    :map-style="mapStyle"
    :center="centre"
    :zoom="zoom"
    :max-zoom="17"
    @load="onMapLoaded"
    @click="closePopup"
  >
    <MglFullscreenControl position="bottom-right" />
    <MglNavigationControl position="bottom-right" />

    <MglGeojsonLayer
      :source-id="geoJsonPoints.data.id"
      :source="geoJsonPoints"
      :layer-id="clustersLayer.id"
      :layer="clustersLayer"
      @click="clickedClusteredLayer"
    />
    <MglGeojsonLayer
      :source-id="geoJsonPoints.data.id"
      :source="geoJsonPoints"
      :layer-id="clusterCountLayer.id"
      :layer="clusterCountLayer"
    />
    <!-- Line, arrows and accuracy circles -->
    <MglGeojsonLayer
      v-if="!shouldClusterLocations"
      :source-id="geoJsonLine.data.id"
      :source="geoJsonLine"
      :layer-id="directionalLineLayer.id"
      :layer="directionalLineLayer"
    />
    <MglGeojsonLayer
      v-if="!isGreyScale && !shouldClusterLocations"
      :source-id="geoJsonLine.data.id"
      :source="geoJsonLine"
      :layer-id="directionalArrowLayer.id"
      :layer="directionalArrowLayer"
    />
    <MglGeojsonLayer
      :key="`accuracyCircles`"
      :source-id="accuracyCircles.data.id"
      :source="accuracyCircles"
      :layer-id="accuracyCircleLayer.id"
      :layer="accuracyCircleLayer"
    />
    <!-- Ordering of these is important - the last ones display ontop -->

    <!-- Item location points -->
    <MglGeojsonLayer
      :key="`unclusteredLayer`"
      :source-id="geoJsonPoints.data.id"
      :source="geoJsonPoints"
      :layer-id="unclusteredLayer.id"
      :layer="unclusteredLayer"
      @click="clickedUnclusteredLayer"
    />
    <MglGeojsonLayer
      v-if="includesFirstPoint && !shouldClusterLocations && itemsWithCoords.length > 1"
      :key="`firstPointLayer`"
      :source-id="geoJsonFirstPoint.data.id"
      :source="geoJsonFirstPoint"
      :layer-id="firstPointLayer.id"
      :layer="firstPointLayer"
      @click="clickedUnclusteredLayer"
    />

    <MglGeojsonLayer
      v-if="!shouldClusterLocations && !latestPointIsUncertain"
      :key="`latestPointLayer`"
      :source-id="geoJsonLatestPoint.data.id"
      :source="geoJsonLatestPoint"
      :layer-id="latestPointLayer.id"
      :layer="latestPointLayer"
      @click="clickedUnclusteredLayer"
    />
    <MglGeojsonLayer
      v-if="!shouldClusterLocations && latestPointIsUncertain"
      :key="`penultimatePointLayer`"
      :source-id="geoJsonPenultimatePoint.data.id"
      :source="geoJsonPenultimatePoint"
      :layer-id="penultimatePointLayer.id"
      :layer="penultimatePointLayer"
      @click="clickedUnclusteredLayer"
    />
    <MglGeojsonLayer
      v-if="!!origin"
      key="originLayer"
      :source-id="geoJsonOrigin.data.id"
      :source="geoJsonOrigin"
      :layer-id="originLayer.id"
      :layer="originLayer"
      @click="clickedUnclusteredLayer"
    />
    <MglGeojsonLayer
      v-if="!!destination"
      key="destinationLayer"
      :source-id="geoJsonDestination.data.id"
      :source="geoJsonDestination"
      :layer-id="destinationLayer.id"
      :layer="destinationLayer"
      @click="clickedUnclusteredLayer"
    />
    <MglGeojsonLayer
      v-if="alerts && alerts.length > 0"
      key="alertLayer"
      :source-id="geoJsonAlerts.data.id"
      :source="geoJsonAlerts"
      :layer-id="alertLayer.id"
      :layer="alertLayer"
      @click="clickedUnclusteredLayer"
    />

    <MglMarker
      v-if="popUpCoordinates !== null"
      :key="popupKey"
      :coordinates="popUpCoordinates"
    >
      <div slot="marker" />
      <MglPopup
        :ref="'popup'"
        :showed="shouldDisplayPopup"
        :close-button="false"
        :close-on-click="true"
      >
        <slot name="popup-content" />
      </MglPopup>
    </MglMarker>
  </MglMap>
</template>
<script>
import mapbox from 'mapbox-gl';
import {
  MglMap,
  MglMarker,
  MglPopup,
  MglNavigationControl,
  MglFullscreenControl,
  MglGeojsonLayer,
} from 'vue-mapbox';
import 'mapbox-gl/dist/mapbox-gl.css';
import variables from '../../../styles/_variables.scss';
import colours from '../../../styles/_updated-variables.scss';
import { createDeepCopy } from '../../../constants';
import { buildAccuracyCircles } from '../../../helpers/accuracy-circle-helper';

let map = null; // map needs to be global to be used in methods

function findNClosestPoints(allPoints, lat, lng, numPoints) {
  const points = allPoints.map((point) => {
    const [longitude, latitude] = point.geometry.coordinates;
    const distance = Math.sqrt((latitude - lat) ** 2 + (longitude - lng) ** 2);
    return { distance, point };
  });
  points.sort((a, b) => a.distance - b.distance);
  return points.slice(0, numPoints).map(({ point }) => point);
}

export default {
  name: 'ReelablesWebMapBox',
  components: {
    MglMap,
    MglMarker,
    MglPopup,
    MglNavigationControl,
    MglFullscreenControl,
    MglGeojsonLayer,
  },
  props: {
    // items are expected to be in reverse chronological order (latest first)
    items: { type: Array, default: () => [] },
    shouldClusterLocations: { type: Boolean, default: false },
    includesFirstPoint: { type: Boolean, default: true },
    isGreyScale: { type: Boolean, default: false },
    latestPointIsUncertain: { type: Boolean, default: false },
    origin: { type: Object, default: null },
    destination: { type: Object, default: null },
    alerts: { type: Array, default: () => [] },
    fitToNewData: { type: Boolean, default: false },
  },
  data() {
    const latestPoint = this.items.length > 0 ? this.items[0] : null;
    return {
      accessToken: process.env.VUE_APP_MAPBOX_ACCESS_TOKEN,
      mapStyle: this.isGreyScale
        ? 'mapbox://styles/steve-reelables/clk18xqtw00be01qyhn9u5exs'
        // ? 'mapbox://styles/steve-reelables/cm9a8w3ab001b01s84xjh4zqu'
        : 'mapbox://styles/steve-reelables/cleidbk6r000e01qb0ycvochz',
      centre: latestPoint
        ? { lon: latestPoint.longitude, lat: latestPoint.latitude }
        : { lon: -0.123, lat: 51.123 },
      hasFitBounds: false,
      zoom: 8,
      shouldDisplayPopup: false,
      popUpCoordinates: null,
      popupKey: 0,
      latestPointLayer: {
        id: 'latestPoint',
        // filter: ['!', ['has', 'point_count']],
        type: 'circle',
        paint: {
          'circle-color': this.isGreyScale ? colours.tealDark : colours.active,
          'circle-radius': 12,
          'circle-stroke-width': this.isGreyScale ? 6 : 3,
          'circle-stroke-color': this.isGreyScale ? colours.tealLight : 'white',
          'circle-stroke-opacity': this.isGreyScale ? 0.50 : 1,
        },
      },
      originLayer: {
        id: 'origin',
        // filter: ['!', ['has', 'point_count']],
        type: 'symbol',
        // paint: {
        // 'fill-color': '#00ffff',
        // },
        // paint: {},
        layout: {
          // 'symbol-placement': 'line',
          // this icon is loaded as part of the style
          // 'icon-image': 'checkered-flag',
          'icon-image': 'map-marker-med',
          // 'icon-image': 'my-triangle-start-colour',
          // 'icon-rotate': 90,
          // 'icon-rotation-alignment': 'map',
          'icon-allow-overlap': true,
          'icon-ignore-placement': true,
          'icon-size': 0.5,
        },
      },
      destinationLayer: {
        id: 'destination',
        // filter: ['!', ['has', 'point_count']],
        type: 'symbol',
        // paint: {
        // 'fill-color': '#00ffff',
        // },
        // paint: {},
        layout: {
          // 'symbol-placement': 'line',
          // this icon is loaded as part of the style
          'icon-image': 'checkered-flag',
          // 'icon-image': 'map-marker-med',
          // 'icon-image': 'my-triangle-start-colour',
          // 'icon-rotate': 90,
          // 'icon-rotation-alignment': 'map',
          'icon-allow-overlap': true,
          'icon-ignore-placement': true,
          'icon-size': 0.5,
        },
      },
      alertLayer: {
        id: 'alertLayer',
        type: 'symbol',
        layout: {
          // 'symbol-placement': 'line',
          // this icon is loaded as part of the style
          'icon-image': 'road-closure',
          // 'icon-image': 'my-triangle-start-colour',
          // 'icon-rotate': 90,
          // 'icon-rotation-alignment': 'map',
          // 'icon-allow-overlap': true,
          // 'icon-ignore-placement': true,
          'icon-size': 1.3,
        },
      },
      // Used when latest point is uncertain
      penultimatePointLayer: {
        id: 'penultimatePointLayer',
        type: 'circle',
        paint: {
          'circle-color': this.isGreyScale ? 'white' : colours.active,
          'circle-radius': 12,
          'circle-stroke-width': this.isGreyScale ? 6 : 3,
          'circle-stroke-color': this.isGreyScale ? colours.tealDark : 'white',
          'circle-stroke-opacity': this.isGreyScale ? 0.50 : 1,
          'circle-opacity': 0.6,
        },
      },
      firstPointLayer: {
        id: 'firstPoint',
        // filter: ['!', ['has', 'point_count']],
        type: 'circle',
        paint: {
          'circle-color': this.isGreyScale ? colours.gray2 : colours.start,
          'circle-radius': this.isGreyScale ? 8 : 12,
          'circle-stroke-width': this.isGreyScale ? 6 : 3,
          'circle-stroke-color': this.isGreyScale ? colours.gray2 : 'white',
          'circle-stroke-opacity': this.isGreyScale ? 0.50 : 1,
        },
      },
      clustersLayer: {
        id: 'clusters',
        filter: ['has', 'point_count'],
        type: 'circle',
        paint: {
          'circle-color': this.shouldClusterLocations ? colours.active : colours.gray,
          'circle-radius': 20,
          'circle-stroke-width': 3,
          'circle-stroke-color': 'white',
        },
        properties: {
          count: ['get', 'point_count'],
        },
      },
      unclusteredLayer: {
        id: 'unclustered-point',
        filter: ['!', ['has', 'point_count']],
        type: 'circle',
        paint: {
          // eslint-disable-next-line no-nested-ternary
          'circle-color': this.isGreyScale
            ? colours.tealLight
            : (this.shouldClusterLocations ? colours.active : colours.gray),
          'circle-radius': this.isGreyScale ? 6 : 6,
          'circle-stroke-width': this.isGreyScale ? 6 : 1,
          'circle-stroke-color': this.isGreyScale ? colours.tealLight : 'white',
          'circle-stroke-opacity': this.isGreyScale ? 0.50 : 1,
        },
      },
      clusterCountLayer: {
        id: 'cluster-count',
        filter: ['has', 'point_count'],
        type: 'symbol',
        layout: {
          'text-field': '{point_count_abbreviated}',
          'text-size': 16,
        },
      },
      directionalLineLayer: {
        id: 'DirectionalLines',
        type: 'line',
        paint: {
          'line-color': this.isGreyScale ? colours.tealLight : colours.start,
          'line-width': this.isGreyScale ? 3 : 1.5,
        },
      },
      directionalArrowLayer: {
        id: 'DirectionalArrows',
        type: 'symbol',
        paint: {},
        layout: {
          'symbol-placement': 'line',
          // this icon is loaded as part of the style
          'icon-image': 'my-triangle-start-colour',
          'icon-rotate': 90,
          'icon-rotation-alignment': 'map',
          'icon-allow-overlap': true,
          'icon-ignore-placement': true,
        },
      },
      accuracyCircleLayer: {
        id: 'accuracy-circle',
        type: 'fill',
        paint: {
          'fill-color': this.isGreyScale ? colours.tealLight : '#00ffff',
          'fill-opacity': 0.15,
        },
      },
    };
  },
  computed: {
    countItems() {
      return this.items.length;
    },
    markerColour() {
      return variables.gotItBlue;
    },
    itemsWithCoords() {
      return this.items
        .filter(({ latitude, longitude }) => !Number.isNaN(Number(latitude))
              && !Number.isNaN(Number(longitude)));
    },
    geoJsonPoints() {
      return {
        type: 'geojson',
        cluster: this.shouldClusterLocations,
        clusterMaxZoom: 50,
        data: {
          id: 'geoJsonPoints',
          type: 'FeatureCollection',
          features:
          (this.shouldClusterLocations
            ? this.itemsWithCoords
            : this.itemsWithCoords.slice(
              this.latestPointIsUncertain ? 2 : 1,
              this.includesFirstPoint ? -1 : this.itemsWithCoords.length,
            ))
            .map(({ latitude, longitude, properties }) => (
              {
                type: 'Feature',
                geometry: {
                  type: 'Point',
                  coordinates: [parseFloat(longitude), parseFloat(latitude)],
                },
                properties,
              }
            )),
        },
      };
    },
    geoJsonOrigin() {
      return {
        type: 'geojson',
        data: {
          id: 'geoJsonOrigin',
          type: 'FeatureCollection',
          features: [{
            type: 'Feature',
            geometry: {
              type: 'Point',
              coordinates: [
                parseFloat(this.origin.longitude),
                parseFloat(this.origin.latitude)],
            },
            properties: this.origin.properties || {},
          }],
        },
      };
    },
    geoJsonDestination() {
      return {
        type: 'geojson',
        data: {
          id: 'geoJsonDestination',
          type: 'FeatureCollection',
          features: [{
            type: 'Feature',
            geometry: {
              type: 'Point',
              coordinates: [
                parseFloat(this.destination.longitude),
                parseFloat(this.destination.latitude)],
            },
            properties: this.destination.properties || {},
          }],
        },
      };
    },
    geoJsonAlerts() {
      return {
        type: 'geojson',
        data: {
          id: 'geoJsonAlerts',
          type: 'FeatureCollection',
          features: this.alerts
            .map(({ latitude, longitude, properties }) => (
              {
                type: 'Feature',
                geometry: {
                  type: 'Point',
                  coordinates: [parseFloat(longitude), parseFloat(latitude)],
                },
                properties,
              }
            )),
        },
      };
    },
    geoJsonLatestPoint() {
      return {
        type: 'geojson',
        data: {
          id: 'geojsonDataLatest',
          type: 'FeatureCollection',
          features: this.itemsWithCoords.length > 0
            ? [{
              type: 'Feature',
              geometry: {
                type: 'Point',
                coordinates: [
                  parseFloat(this.itemsWithCoords[0].longitude),
                  parseFloat(this.itemsWithCoords[0].latitude)],
              },
              properties: this.itemsWithCoords[0].properties,
            }]
            : [],
        },
      };
    },
    geoJsonPenultimatePoint() {
      return {
        type: 'geojson',
        data: {
          id: 'geoJsonPenultimatePoint',
          type: 'FeatureCollection',
          features: this.itemsWithCoords.length > 1
            ? [{
              type: 'Feature',
              geometry: {
                type: 'Point',
                coordinates: [
                  parseFloat(this.itemsWithCoords[1].longitude),
                  parseFloat(this.itemsWithCoords[1].latitude)],
              },
              properties: this.itemsWithCoords[1].properties,
            }]
            : [],
        },
      };
    },
    geoJsonFirstPoint() {
      return {
        type: 'geojson',
        data: {
          id: 'geojsonDataFirst',
          type: 'FeatureCollection',
          features: this.itemsWithCoords.length > 0
            ? [{
              type: 'Feature',
              geometry: {
                type: 'Point',
                coordinates: [
                  parseFloat(this.itemsWithCoords[this.itemsWithCoords.length - 1].longitude),
                  parseFloat(this.itemsWithCoords[this.itemsWithCoords.length - 1].latitude)],
              },
              properties: this.itemsWithCoords[this.itemsWithCoords.length - 1].properties,
            }]
            : [],
        },
      };
    },
    geoJsonLine() {
      const arr = createDeepCopy(this.latestPointIsUncertain
        ? this.itemsWithCoords.slice(1)
        : this.itemsWithCoords);
      arr.sort((a, b) => ((a.properties.timestamp > b.properties.timestamp) ? 1 : -1));
      const arrows = [];
      for (let i = 0; i < arr.length - 1; i++) {
        arrows.push({
          start: [Number(arr[i].longitude),
            Number(arr[i].latitude)],
          end: [Number(arr[i + 1].longitude), Number(arr[i + 1].latitude)],
        });
      }
      function drawShortestLine(unfilteredStart, unfilteredEnd) {
        const start = createDeepCopy(unfilteredStart);
        const end = createDeepCopy(unfilteredEnd);
        if (Math.abs(unfilteredEnd[0] - unfilteredStart[0]) > 180
         && unfilteredEnd[0] > unfilteredStart[0]) {
          start[0] += 360 * Math.ceil((unfilteredEnd[0] - unfilteredStart[0]) / 360);
        }
        if (Math.abs(unfilteredEnd[0] - unfilteredStart[0]) > 180
         && unfilteredStart[0] > unfilteredEnd[0]) {
          end[0] += 360 * Math.ceil((unfilteredStart[0] - unfilteredEnd[0]) / 360);
        }
        const shortestLine = [start, end];
        return shortestLine;
      }

      return {
        type: 'geojson',
        data: {
          id: 'DirectionalArrows',
          type: 'FeatureCollection',
          features: arrows.map(({ start, end }) => (
            {
              type: 'Feature',
              geometry: {
                type: 'LineString',
                coordinates: drawShortestLine(start, end),
              },
            }
          )),
        },
      };
    },
    accuracyCircles() {
      return buildAccuracyCircles(this.itemsWithCoords, this.latestPointIsUncertain);
    },
    latestPointKey() {
      return this.itemsWithCoords.length > 0
        ? `${this.itemsWithCoords[0].latitude}_${this.itemsWithCoords[0].longitude}`
        : null;
    },
  },
  watch: {
    items() { this.fitBounds(); },
    latestPointKey() {
      if (this.fitToNewData) {
        this.hasFitBounds = false;
        this.fitBounds();
      }
    },
    latestPointIsUncertain() {
      this.updateAllSourceData();
      if (this.latestPointIsUncertain) {
        this.removeLayer(this.latestPointLayer.id);
      } else {
        this.removeLayer(this.penultimatePointLayer.id);
      }
    },
    includesFirstPoint() { this.updateAllSourceData(); },
    countItems() { this.updateAllSourceData(); },
  },
  created() {
    // We need to set mapbox-gl library here in order to use it in template
    // Do not remove
    this.mapbox = mapbox;
  },
  beforeDestroy() {
    // Need to remove layers before distroying and avoid errors being thrown
    try {
      this.removeLayer(this.alertLayer.id);
      this.removeLayer(this.originLayer.id);
      this.removeLayer(this.destinationLayer.id);
      this.removeLayer(this.latestPointLayer.id);
      this.removeLayer(this.penultimatePointLayer.id);
      this.removeLayer(this.firstPointLayer.id);
      this.removeLayer(this.clustersLayer.id);
      this.removeLayer(this.clusterCountLayer.id);
      this.removeLayer(this.unclusteredLayer.id);
      this.removeLayer(this.directionalLineLayer.id);
      this.removeLayer(this.directionalArrowLayer.id);
      this.removeLayer(this.accuracyCircleLayer.id);
    } catch (err) {
      // map is undefined
    }
  },
  methods: {
    onMapLoaded(event) {
      this.$emit('loaded');
      map = event.map;
      this.fitBounds();

      map.on('zoom', this.closePopup);
    },
    updateAllSourceData() {
      this.updateSourceData(this.geoJsonPoints.data.id, this.geoJsonPoints.data);
      this.updateSourceData(this.geoJsonLine.data.id, this.geoJsonLine.data);
      this.updateSourceData(this.geoJsonFirstPoint.data.id, this.geoJsonFirstPoint.data);
      this.updateSourceData(this.geoJsonLatestPoint.data.id, this.geoJsonLatestPoint.data);
      this.updateSourceData(
        this.geoJsonPenultimatePoint.data.id,
        this.geoJsonPenultimatePoint.data,
      );
      this.updateSourceData(this.accuracyCircles.data.id, this.accuracyCircles.data);
    },
    updateSourceData(sourceId, data) {
      const source = map.getSource(sourceId);
      if (source) {
        // this will update the data in the source and re-render the map
        source.setData(data);
      }
    },
    removeLayer(layerId) {
      if (map && map.getLayer(layerId)) {
        map.removeLayer(layerId);
      }
    },
    fitBounds() {
      if (!this.hasFitBounds && this.itemsWithCoords.length > 0) {
        this.hasFitBounds = true;
        if (this.isGreyScale) {
          // centre
          map.flyTo({
            center: [
              this.itemsWithCoords[0].longitude,
              this.itemsWithCoords[0].latitude,
            ],
          });
        } else {
          // fix issue if crossing the date line
          let crossesDateLine = false;
          for (let i = 1; i < this.itemsWithCoords.length; i++) {
            const lngDiff = Math.abs(parseFloat(this.itemsWithCoords[i].longitude)
              - parseFloat(this.itemsWithCoords[i - 1].longitude));
            if (lngDiff > 180) {
              crossesDateLine = true;
              break;
            }
          }

          // fit to all points
          const longitudes = this.itemsWithCoords
            .map(({ longitude }) => (crossesDateLine && parseFloat(longitude) < 0
              ? parseFloat(longitude) + 360
              : parseFloat(longitude)));
          const latitudes = this.itemsWithCoords.map(({ latitude }) => (parseFloat(latitude)));

          if (map) {
            const minLng = Math.min(...longitudes);
            const maxLng = Math.max(...longitudes);
            const minLat = Math.min(...latitudes);
            const maxLat = Math.max(...latitudes);

            map.fitBounds([
              minLng,
              minLat,
              maxLng,
              maxLat,
            ], { padding: 50 });
          }
        }
      }
    },
    showPopup(coords) {
      this.popUpCoordinates = coords;
      this.shouldDisplayPopup = true;
      this.popupKey += 1;
    },
    closePopup() {
      this.shouldDisplayPopup = false;
    },
    parseEventData(event) {
      return {
        popupCoordinates: [event.mapboxEvent.lngLat.lng, event.mapboxEvent.lngLat.lat],
        features: event.map.queryRenderedFeatures(event.mapboxEvent.point, {
          layers: [event.layerId],
        }),
      };
    },
    clickedUnclusteredLayer(event) {
      const feature = this.parseEventData(event).features[0];
      this.showPopup(feature.geometry.coordinates);
      this.$emit('clickedUnclusteredItem', feature.properties);
    },
    clickedClusteredLayer(event) {
      const feature = this.parseEventData(event).features[0];
      event.map.getSource(this.geoJsonPoints.data.id)
        .getClusterLeaves(
          feature.id,
          feature.properties.point_count,
          0,
          // (error, clusterFeatures) => {
          () => {
            const clusteredPoints = findNClosestPoints(
              this.geoJsonPoints.data.features,
              feature.geometry.coordinates[1],
              feature.geometry.coordinates[0],
              feature.properties.point_count,
            );
            this.showPopup(feature.geometry.coordinates);
            event.map.on('zoom', this.closePopup);
            this.$emit('clickedClusteredItems', clusteredPoints
              ? clusteredPoints.map(({ properties }) => properties)
              : []);
            // this.$emit('clickedClusteredItems', clusterFeatures
            // ? clusterFeatures.map(({ properties }) => properties)
            // : []);
          },
        );
    },
  },
};
</script>
<style lang="scss">
@import '@/styles/_variables.scss';

.mapboxgl-popup-close-button {
  color: $black;
}

.mapboxgl-canvas {
  position: relative !important;
}

.mapboxgl-ctrl-logo, .mapboxgl-ctrl-attrib {
  display: none !important;
}

@media (max-width: 959px) {
  .mapboxgl-ctrl-group,
  .mapboxgl-ctrl-fullscreen {
    display: none;
  }
}
</style>
